import * as React from 'react';
import {FC} from 'react';
import {createRoot} from 'react-dom/client';

import {
  ApiClient,
  ApiProvider,
  Auth,
  AuthProvider,
  Footer,
  Header,
  INavigationItem,
  ModalProvider,
  NavigationProvider,
  UnifiedUserIssueResolver,
  fanaticsCollectMonolithTheme,
  useNavItems,
} from '@fanatics-live/common-components';

import {BrowserRouter} from 'react-router-dom';
import {ChakraProvider, extendTheme} from '@chakra-ui/react';

import {INavConfig} from "../../../types/nav";
import {ICheckoutConfig} from '../../../types/checkout';
import {IFanIdConfig} from "../../../types/fan-id";
import {IFeaturesConfig} from "../../../types/features";

interface INavWrapper {
  children: any;
}

declare const jsConfig: { features: IFeaturesConfig, nav: INavConfig, menu: INavigationItem[], checkout: ICheckoutConfig, fanId: IFanIdConfig };

const featureFlags = jsConfig.features.flags
const isOfferNotificationBadgesEnabled = featureFlags.webEnableOfferBadges;
const isOffersNotificationDialogEnabled = featureFlags.webEnableOffersNotificationDialog;
const isPlaidPAApprovalEnabled = featureFlags.webEnablePlaidPAApproval;
const navConfig = jsConfig.nav;
const profile = navConfig?.profile;
const fanaticsCollectBaseUrl = navConfig?.baseUrl;
const showPencilBanner = navConfig?.messageEnabled;
const enableAchTerms = navConfig?.enableAchTerms;
const enableUnifiedUserConflictModal = navConfig?.enableUnifiedUserConflictModal;
const searchBaseUrl = navConfig?.searchBaseUrl;
const savedSearchBaseUrl = navConfig?.savedSearchBaseUrl;
const authBaseUrl = navConfig?.authBaseUrl;
const mobileBaseUrl = navConfig?.mobileBaseUrl;
const membersBaseUrl = navConfig?.membersBaseUrl;
const graphqlUrl = navConfig?.graphqlUrl;

const checkoutConfig = jsConfig.checkout;
const checkoutApiUrl = checkoutConfig?.apiUrl
const addPaymentMethodLoginFlow = checkoutConfig?.paymentVerificationHubFlow
const stripeMarketplaceKey = checkoutConfig?.stripePublicKey

const fanIdConfig = jsConfig.fanId;
const fanIdBaseUrl = fanIdConfig.baseUrl;
const fanIdClientId = fanIdConfig.clientId;
const fanIdPreview = fanIdConfig.preview;

const logoutFn: any = (forceLogout: boolean) => {
  if (forceLogout) {
    window.location.href = navConfig?.logoutUrl;
  }
};

const auth = new Auth(authBaseUrl, mobileBaseUrl, 'v2', logoutFn);
auth.profile = profile;
export const apiClient = new ApiClient(auth, '');

const NavWrapper: FC<INavWrapper> = ({children}) => {
  const message = navConfig.message;
  const menu = jsConfig.menu;
  const navItems = useNavItems(fanaticsCollectBaseUrl, menu);
  const isAdmin = profile?.isAdmin;
  const isAdminPage = window !== undefined && window.location.hostname.includes('admin');
  const enableNewNav = !isAdminPage && navConfig?.isBoNavigationEnabled;
  const enableCollections = navConfig?.isCollectionsEnabled;
  const isNewPayoutFlowEnabled = navConfig?.isNewPayoutFlowEnabled;

  return (
    <BrowserRouter>
      <ChakraProvider theme={extendTheme(fanaticsCollectMonolithTheme)} resetCSS={false}>
        <AuthProvider value={auth}>
          <ApiProvider value={apiClient}>
              <NavigationProvider value={{fanaticsCollectBaseUrl, navItems, isAdmin, enableNewNav, isNewPayoutFlowEnabled, pastSearchBaseUrl: savedSearchBaseUrl}}>
                {children({ navItems, auth, message, enableCollections })}
              </NavigationProvider>
          </ApiProvider>
        </AuthProvider>
      </ChakraProvider>
    </BrowserRouter>
  );
};

const mainElement = document.getElementById('nav-main-header');
if (mainElement) {
  const headerRoot = createRoot(mainElement);
  headerRoot.render(
      <NavWrapper>
        {({navItems, message, auth: innerAuth, enableCollections}) => (
          <ModalProvider
            enableAchTerms={enableAchTerms}
            membersApiUrl={membersBaseUrl}
            fanaticsCollectBaseUrl={fanaticsCollectBaseUrl}
            addPaymentMethodLoginFlow={addPaymentMethodLoginFlow}
            checkoutApiUrl={checkoutApiUrl}
            graphqlApiUrl={graphqlUrl}
            fanIdBaseUrl={fanIdBaseUrl}
            fanIdClientId={fanIdClientId}
            fanIdPreview={fanIdPreview}
            stripeMarketplaceKey={stripeMarketplaceKey}
          >
            <Header
              {...({
                auth: innerAuth,
                enableCollections,
                enableOfferNotificationBadges: isOfferNotificationBadgesEnabled,
                enablePlaidPAApproval: isPlaidPAApprovalEnabled,
                navItems,
                offersNotificationDialogEnabled: isOffersNotificationDialogEnabled,
                searchBaseUrl,
                pastSearchBaseUrl: savedSearchBaseUrl,
                fanIdBaseUrl,
                fanIdClientId,
                fanIdPreview,
                showPencilBanner,
                siteWideMessage: message,
              } as any)}
            />
            {enableUnifiedUserConflictModal && <UnifiedUserIssueResolver />}
          </ModalProvider>
        )}
      </NavWrapper>
  );
}

const footerElement = document.getElementById('nav-footer');
if (footerElement) {
  const footerRoot = createRoot(footerElement);
  footerRoot.render(<NavWrapper>{() => (<Footer/>)}</NavWrapper>);
}
